/**
 * SVG Path Utility Functions
 * Shared utilities for parsing and manipulating SVG path data
 */

/**
 * Represents a single SVG path command
 */
export interface PathCommand {
    type: string;
    values: number[];
}

/**
 * Parse SVG path d attribute into commands
 * Handles all SVG path commands: M, L, H, V, C, S, Q, T, A, Z (both absolute and relative)
 */
export function parsePathCommands(d: string): PathCommand[] {
    const commands: PathCommand[] = [];
    // Match command letter followed by numbers (with optional signs and decimals)
    const regex = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
    let match;

    while ((match = regex.exec(d)) !== null) {
        const type = match[1];
        const valueStr = match[2].trim();

        let values: number[] = [];
        if (valueStr) {
            // Parse numbers, handling negative signs and decimals
            const numRegex = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;
            let numMatch;
            while ((numMatch = numRegex.exec(valueStr)) !== null) {
                values.push(parseFloat(numMatch[0]));
            }
        }

        commands.push({ type, values });
    }

    return commands;
}

/**
 * Calculate bounding box from path commands
 * Note: This is an approximation that doesn't account for curves properly
 */
export function calculatePathBounds(commands: PathCommand[]): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    let currentX = 0, currentY = 0;

    for (const cmd of commands) {
        const { type, values } = cmd;
        const isRelative = type === type.toLowerCase();
        const cmdType = type.toUpperCase();

        switch (cmdType) {
            case 'M':
            case 'L':
            case 'T':
                for (let i = 0; i < values.length; i += 2) {
                    const x = isRelative ? currentX + values[i] : values[i];
                    const y = isRelative ? currentY + values[i + 1] : values[i + 1];
                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                    currentX = x;
                    currentY = y;
                }
                break;
            case 'H':
                for (const v of values) {
                    const x = isRelative ? currentX + v : v;
                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                    currentX = x;
                }
                break;
            case 'V':
                for (const v of values) {
                    const y = isRelative ? currentY + v : v;
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                    currentY = y;
                }
                break;
            case 'C':
                for (let i = 0; i < values.length; i += 6) {
                    // Include control points in bounds
                    const points = isRelative
                        ? [
                            [currentX + values[i], currentY + values[i + 1]],
                            [currentX + values[i + 2], currentY + values[i + 3]],
                            [currentX + values[i + 4], currentY + values[i + 5]],
                        ]
                        : [
                            [values[i], values[i + 1]],
                            [values[i + 2], values[i + 3]],
                            [values[i + 4], values[i + 5]],
                        ];
                    for (const [x, y] of points) {
                        minX = Math.min(minX, x);
                        maxX = Math.max(maxX, x);
                        minY = Math.min(minY, y);
                        maxY = Math.max(maxY, y);
                    }
                    currentX = points[2][0];
                    currentY = points[2][1];
                }
                break;
            case 'S':
            case 'Q':
                for (let i = 0; i < values.length; i += 4) {
                    const points = isRelative
                        ? [
                            [currentX + values[i], currentY + values[i + 1]],
                            [currentX + values[i + 2], currentY + values[i + 3]],
                        ]
                        : [
                            [values[i], values[i + 1]],
                            [values[i + 2], values[i + 3]],
                        ];
                    for (const [x, y] of points) {
                        minX = Math.min(minX, x);
                        maxX = Math.max(maxX, x);
                        minY = Math.min(minY, y);
                        maxY = Math.max(maxY, y);
                    }
                    currentX = points[1][0];
                    currentY = points[1][1];
                }
                break;
            case 'A':
                for (let i = 0; i < values.length; i += 7) {
                    const x = isRelative ? currentX + values[i + 5] : values[i + 5];
                    const y = isRelative ? currentY + values[i + 6] : values[i + 6];
                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                    currentX = x;
                    currentY = y;
                }
                break;
            // Z doesn't change position for bounds calculation
        }
    }

    return { minX, minY, maxX, maxY };
}
